import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import safeNoop from "../lib/safeNoop";
import { Activity, Mail, Copy, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState(null);
  const [devMinutes, setDevMinutes] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // Dev stub: backend returns a relative reset URL when the email exists.
      // We compose the full origin here so the user can click / copy it.
      if (data?.dev_reset_url) {
        setDevUrl(`${window.location.origin}${data.dev_reset_url}`);
        setDevMinutes(data.expires_in_minutes ?? 30);
      }
    } catch (e2) {
      setErr(e2.userMessage || "Could not send reset link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!devUrl) return;
    try {
      await navigator.clipboard.writeText(devUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard API unavailable (insecure context, denied permission).
      // The link is rendered on screen so the user can still select-and-copy
      // by hand — no user-facing failure path needed.
      safeNoop("forgot.clipboard-unavailable", e);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: Form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-md bg-foreground text-background grid place-items-center font-heading font-bold">D</div>
            <div>
              <div className="font-heading font-semibold leading-tight">Dreamhouse</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Flow</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="forgot-title">
            Reset your password.
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            We&apos;ll send a one-time reset link. It expires in 30 minutes and can be used once.
          </p>

          {!sent ? (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="label-tiny block mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-email"
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm"
                />
              </div>
              {err && (
                <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5" data-testid="forgot-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full py-2.5 rounded-md bg-foreground text-background font-medium text-sm hover:bg-zinc-200 disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
              <p className="text-xs text-muted-foreground text-center pt-2">
                <Link to="/login" className="text-foreground underline underline-offset-2" data-testid="forgot-back-login">
                  Back to sign in
                </Link>
              </p>
            </form>
          ) : (
            <div className="mt-8 space-y-4" data-testid="forgot-success">
              <div className="flex items-start gap-3 p-3 rounded-md border border-[#10B981]/30 bg-[#10B981]/5">
                <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                <div className="text-sm leading-relaxed">
                  If an account exists for <span className="font-medium text-foreground">{email}</span>,
                  a reset link has been issued. Check your inbox (and spam).
                </div>
              </div>

              {devUrl && (
                <div className="rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3" data-testid="forgot-dev-url">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-medium">
                    <Activity className="w-3 h-3" /> Dev mode · no SMTP wired
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    Copy the link below to continue the reset flow. Expires in {devMinutes} minutes.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 min-w-0 truncate text-[11px] font-mono bg-zinc-950 border border-border rounded px-2 py-1.5" data-testid="forgot-dev-url-value">
                      {devUrl}
                    </code>
                    <button
                      onClick={copyLink}
                      data-testid="forgot-dev-copy"
                      className="px-2.5 py-1.5 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5"
                    >
                      {copied ? <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> : <Copy className="w-3 h-3" />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <Link
                    to={devUrl.replace(window.location.origin, "")}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-foreground underline underline-offset-2"
                    data-testid="forgot-dev-open"
                  >
                    Open reset page →
                  </Link>
                </div>
              )}

              <div className="pt-2 flex items-center justify-between text-xs">
                <button
                  onClick={() => { setSent(false); setDevUrl(null); }}
                  className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                  data-testid="forgot-try-again"
                >
                  Try a different email
                </button>
                <Link to="/login" className="text-foreground underline underline-offset-2" data-testid="forgot-back-login-after">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block bg-cover bg-center relative"
           style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/baf1959c-448b-42d0-a6ba-e254d37dd6a6/images/3a57008b0888605369a37529259b72e5b64a3911642763c0a075388fd680c4c0.png)" }}>
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="label-tiny text-white/70 mb-2 flex items-center gap-2"><Mail className="w-3 h-3" /> Secure one-time reset</div>
          <div className="font-heading text-3xl font-semibold leading-tight max-w-md">
            A single-use link.<br /> 30 minutes. Then it&apos;s gone.
          </div>
          <div className="text-sm text-white/60 mt-2 max-w-md">
            Tokens are hashed at rest, expire automatically, and can only be used once.
          </div>
        </div>
      </div>
    </div>
  );
}
